import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, Signal, WritableSignal, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DefaultEnvironment, ENVIRONMENT_TOKEN } from '../models/config.model';
import { SessionWebWorkerService } from './session-web-worker.service';

/** Set (shared across tabs) once this browser has talked to ATOM; cleared on logout. */
const ATOM_SESSION_KEY: string = 'atomSession';
const ATOM_SESSION_PRESENT: string = '1';
/** Where AuthState keeps the access token (set on login, cleared by the logout plugin). */
const ACCESS_TOKEN_KEY: string = 'auth';
const ACTIVITY_PATH: string = '/api/v1/session/activity';
const END_PATH: string = '/api/v1/session/end';
/** Report activity at most this often; must stay below ATOM's grace period (10 min). */
const ACTIVITY_INTERVAL_MS: number = 5 * 60 * 1000;
/** After a failed report, wait this long before trying again. */
const FAILURE_RETRY_MS: number = 60 * 1000;
/** Failures lasting this long raise the connection banner (half of ATOM's grace period). */
const CONNECTION_LOST_AFTER_MS: number = 5 * 60 * 1000;

interface ActivityBody {
	readonly idleTimeoutMinutes: number;
}

/**
 * Keeps the ATOM REST session in step with this app's own session (design
 * 2026-10-07-tms-session-timeout-sync.md): tells ATOM which timeout the client enforces, reports
 * real user activity, and ends the ATOM session when the client logs out. Every call is gated on
 * this browser having talked to ATOM, so a user who never opens a TMS page sends nothing.
 */
@Injectable({
	providedIn: 'root',
})
export class AtomSessionService {
	private readonly http: HttpClient = inject(HttpClient);
	private readonly environment: DefaultEnvironment = inject(ENVIRONMENT_TOKEN) as DefaultEnvironment;
	private readonly sessionService: SessionWebWorkerService = inject(SessionWebWorkerService);

	private readonly connectionLostState: WritableSignal<boolean> = signal<boolean>(false);
	/** True while activity reports have been failing for longer than ATOM can safely wait. */
	readonly connectionLost: Signal<boolean> = this.connectionLostState.asReadonly();

	private reportedThisTab: boolean = false;
	private inFlight: boolean = false;
	private lastSuccessMs: number = 0;
	private lastAttemptMs: number = 0;
	private firstFailureMs: number | null = null;

	constructor() {
		this.sessionService.debounceActivity$.pipe(takeUntilDestroyed(inject(DestroyRef))).subscribe((): void => this.onActivity());
	}

	/** Whether this browser holds an ATOM session (any tab). */
	static hasSession(): boolean {
		try {
			return localStorage.getItem(ATOM_SESSION_KEY) === ATOM_SESSION_PRESENT;
		} catch {
			return false;
		}
	}

	/**
	 * Called for every authenticated request to ATOM. Marks the browser as holding an ATOM
	 * session and, the first time in this tab, reports the client's timeout straight away.
	 */
	touch(): void {
		// Re-mark when another tab's logout cleared the shared marker while this tab lives on. A report already in
		// flight covers this request — and its own POST passes through here, so without this guard a failed
		// marker write (storage full) would start a new report for every report.
		if (this.inFlight || (this.reportedThisTab && AtomSessionService.hasSession())) {
			return;
		}
		this.reportedThisTab = true;
		try {
			localStorage.setItem(ATOM_SESSION_KEY, ATOM_SESSION_PRESENT);
		} catch {
			// Storage unavailable: the report below still reaches ATOM for this tab.
		}
		this.report();
	}

	/** Real user activity anywhere in the app: report it to ATOM, throttled. */
	onActivity(): void {
		if (this.inFlight || !AtomSessionService.hasSession()) {
			return;
		}
		const now: number = Date.now();
		if (this.firstFailureMs !== null) {
			if (now - this.lastAttemptMs < FAILURE_RETRY_MS) {
				return;
			}
		} else if (now - this.lastSuccessMs < ACTIVITY_INTERVAL_MS) {
			return;
		}
		this.report();
	}

	/**
	 * End the ATOM session (client logout or idle timeout). Called by the NGXS logout plugin
	 * BEFORE it clears storage, because that is where the marker and the access token live — by
	 * the time any Logout handler runs, both are gone. Static so the plugin needs no injection.
	 * Uses a keepalive fetch so the request survives the navigation that follows logout, and
	 * sends the load-balancer cookie so it reaches the node holding the session. Best effort: if
	 * it never arrives, ATOM ends the session itself after the client's timeout plus its grace.
	 */
	static endSession(atomApiUrl: string): void {
		if (!AtomSessionService.hasSession()) {
			return;
		}
		const accessToken: string | null = localStorage.getItem(ACCESS_TOKEN_KEY);
		localStorage.removeItem(ATOM_SESSION_KEY);
		if (!accessToken) {
			return;
		}
		fetch(`${atomApiUrl}${END_PATH}`, {
			method: 'POST',
			keepalive: true,
			credentials: 'include',
			headers: { Authorization: `Bearer ${accessToken}` },
		}).catch((): void => undefined);
	}

	private report(): void {
		const now: number = Date.now();
		this.inFlight = true;
		this.lastAttemptMs = now;
		const body: ActivityBody = { idleTimeoutMinutes: this.sessionService.getTimeoutMinutes() };
		this.http.post<void>(`${this.environment.atomApiUrl}${ACTIVITY_PATH}`, body).subscribe({
			next: (): void => {
				this.inFlight = false;
				this.lastSuccessMs = Date.now();
				this.firstFailureMs = null;
				this.connectionLostState.set(false);
			},
			error: (): void => {
				this.inFlight = false;
				const failedAt: number = Date.now();
				this.firstFailureMs = this.firstFailureMs ?? failedAt;
				if (failedAt - this.firstFailureMs >= CONNECTION_LOST_AFTER_MS) {
					this.connectionLostState.set(true);
				}
			},
		});
	}
}
